/**
 * Match logged-in user to EMS trainer rows: application_user_id first,
 * then organization_user_id vs trainer_employee_id (legacy shapes).
 */

function trimId(v) {
  if (v == null || v === "") return "";
  return String(v).trim();
}

export function getLoggedInApplicationUserId(userData) {
  if (!userData) return "";
  const orgUser = userData.organizationuser || userData.organization_user;
  const top =
    userData.application_user_id ??
    orgUser?.application_user_id ??
    (Array.isArray(userData.metadata) ? userData.metadata[0]?.application_user_id : null);
  if (top != null && trimId(top)) return trimId(top);
  const loginuser = userData.loginuser;
  const first = Array.isArray(loginuser) ? loginuser[0] : loginuser;
  if (!first) return "";
  const fromLogin =
    first.application_user_id ??
    first.application_user?.application_user_id ??
    (Array.isArray(first.applicationuser)
      ? first.applicationuser[0]?.application_user_id
      : null) ??
    (typeof first.applicationuser === "object" && first.applicationuser
      ? first.applicationuser.application_user_id
      : null) ??
    first.organization_user?.application_user_id ??
    first.organization_person?.application_user?.application_user_id ??
    "";
  return trimId(fromLogin);
}

export function getLoggedInOrganizationUserIds(userData) {
  const ids = new Set();
  const add = (value) => {
    const id = trimId(value);
    if (id) ids.add(id);
  };
  const orgUser = userData?.organizationuser || userData?.organization_user;
  if (Array.isArray(orgUser)) {
    orgUser.forEach((item) => add(item?.organization_user_id));
  } else {
    add(orgUser?.organization_user_id);
  }
  add(userData?.organization_user_id);
  if (Array.isArray(userData?.metadata)) {
    userData.metadata.forEach((item) => add(item?.organization_user_id));
  }
  const loginuser = userData?.loginuser;
  const first = Array.isArray(loginuser) ? loginuser[0] : loginuser;
  add(first?.organization_user_id);
  add(first?.organization_user?.organization_user_id);
  add(first?.organization_person?.organization_user_id);
  return [...ids];
}

export function getLoggedInOrganizationUserId(userData) {
  return getLoggedInOrganizationUserIds(userData)[0] || "";
}

/** Org-user and application-user ids from batch / class / session trainer links. */
export function linkedTrainerIdentity(...groups) {
  const employeeIds = new Set();
  const applicationIds = new Set();
  groups.flat().forEach((link) => {
    if (!link || typeof link !== "object") return;
    const orgId = trimId(
      link.trainer_employee_id ??
        link.trainer?.organization_user_id ??
        link.organization_user_id
    );
    if (orgId) employeeIds.add(orgId);
    const appId = getApplicationUserIdFromTrainerObject(link.trainer ?? link);
    if (appId) applicationIds.add(appId);
  });
  return {
    trainer_employee_ids: [...employeeIds],
    trainer_application_user_ids: [...applicationIds],
  };
}

export function getApplicationUserIdFromTrainerObject(t) {
  if (!t || typeof t !== "object") return "";
  const candidates = [
    t.application_user_id,
    Array.isArray(t.applicationuser) ? t.applicationuser[0]?.application_user_id : null,
    t.applicationuser?.application_user_id,
    Array.isArray(t.application_user) ? t.application_user[0]?.application_user_id : null,
    t.application_user?.application_user_id,
    t.organization_user?.application_user_id,
    Array.isArray(t.organization_user?.application_user)
      ? t.organization_user.application_user[0]?.application_user_id
      : null,
    t.organization_user?.applicationuser?.[0]?.application_user_id,
    t.organization_person?.application_user?.application_user_id,
  ];
  for (const c of candidates) {
    const s = trimId(c);
    if (s) return s;
  }
  return "";
}

export function getTrainerApplicationUserIdFromRow(row) {
  if (!row || typeof row !== "object") return "";
  const direct = trimId(row.trainer_application_user_id);
  if (direct) return direct;
  const t = row.trainer;
  if (t && typeof t === "object") {
    const fromT = getApplicationUserIdFromTrainerObject(t);
    if (fromT) return fromT;
  }
  const nested =
    row.batchClass?.trainer ??
    row.batch_class?.trainer ??
    row.batchClassRecord?.batchClass?.trainer ??
    row.batch_class_record?.batch_class?.trainer ??
    row.batchClassRecord?.batch_class?.trainer ??
    row.batchClassRecord?.trainer ??
    row.batch_class_record?.trainer ??
    row.class_record?.trainer ??
    row.demo_session?.trainer ??
    row.demoSession?.trainer;
  const fromNested = getApplicationUserIdFromTrainerObject(nested);
  if (fromNested) return fromNested;
  return "";
}

export function getTrainerOrganizationUserIdFromRow(row) {
  if (!row || typeof row !== "object") return "";
  const t = row.trainer;
  const fromBatch =
    row.batchClass?.trainer_employee_id ??
    row.batch_class?.trainer_employee_id ??
    row.batchClassRecord?.batchClass?.trainer_employee_id ??
    row.batch_class_record?.batch_class?.trainer_employee_id ??
    row.batchClassRecord?.batch_class?.trainer_employee_id;
  const v =
    row.trainer_employee_id ??
    fromBatch ??
    t?.organization_user_id ??
    t?.organization_user?.organization_user_id ??
    "";
  return trimId(v);
}

/** Class record belongs to the logged-in trainer when they are on that session. */
export function emsClassRecordMatchesLoggedInTrainer(row, userData) {
  const loggedApp = getLoggedInApplicationUserId(userData);
  const loggedOrgIds = getLoggedInOrganizationUserIds(userData);
  const appIds = [
    ...(Array.isArray(row?.trainer_application_user_ids)
      ? row.trainer_application_user_ids
      : []),
    row?.trainer_application_user_id,
  ]
    .map(trimId)
    .filter(Boolean);
  const orgIds = [
    ...(Array.isArray(row?.trainer_employee_ids) ? row.trainer_employee_ids : []),
    row?.trainer_employee_id,
  ]
    .map(trimId)
    .filter(Boolean);

  if (loggedApp && appIds.some((id) => id === loggedApp)) return true;
  if (loggedOrgIds.length && orgIds.some((id) => loggedOrgIds.includes(id))) return true;
  return emsBatchListRowMatchesLoggedInTrainer(row, userData);
}

export function emsRowMatchesLoggedInTrainer(row, userData) {
  const loggedApp = getLoggedInApplicationUserId(userData);
  const loggedOrgIds = getLoggedInOrganizationUserIds(userData);
  const appIds = [
    ...(Array.isArray(row?.trainer_application_user_ids)
      ? row.trainer_application_user_ids
      : []),
    row?.trainer_application_user_id,
    getTrainerApplicationUserIdFromRow(row),
  ]
    .map(trimId)
    .filter(Boolean);
  const orgIds = [
    ...(Array.isArray(row?.trainer_employee_ids) ? row.trainer_employee_ids : []),
    row?.trainer_employee_id,
    getTrainerOrganizationUserIdFromRow(row),
  ]
    .map(trimId)
    .filter(Boolean);

  if (loggedApp && appIds.some((id) => id === loggedApp)) return true;
  if (loggedOrgIds.length && orgIds.some((id) => loggedOrgIds.includes(id))) return true;
  return false;
}

function nestedClassTrainerLinks(classes) {
  if (!Array.isArray(classes)) return [];
  return classes.flatMap((cls) => [
    ...(Array.isArray(cls?.class_trainers) ? cls.class_trainers : []),
    ...(Array.isArray(cls?.classTrainers) ? cls.classTrainers : []),
  ]);
}

function batchIdOf(row) {
  return trimId(
    row?.organization_ems_batch_id ?? row?.batch?.organization_ems_batch_id ?? ""
  );
}

export function emsBatchListRowMatchesLoggedInTrainer(row, userData) {
  const identity = linkedTrainerIdentity(
    row?.batch_trainers,
    row?.batch?.batch_trainers,
    row?.batch_classes,
    row?.batch?.batch_classes,
    nestedClassTrainerLinks(row?.batch_classes),
    nestedClassTrainerLinks(row?.batch?.batch_classes),
    row?.class_trainers,
    row?.classTrainers,
    row?.trainer ? [row.trainer] : []
  );
  const merged = {
    ...row,
    trainer_employee_ids: [
      ...(Array.isArray(row?.trainer_employee_ids) ? row.trainer_employee_ids : []),
      ...identity.trainer_employee_ids,
    ],
    trainer_application_user_ids: [
      ...(Array.isArray(row?.trainer_application_user_ids)
        ? row.trainer_application_user_ids
        : []),
      ...identity.trainer_application_user_ids,
    ],
  };
  return emsRowMatchesLoggedInTrainer(merged, userData);
}

/** Add nested batch objects from batch-class rows that are missing in the batches list. */
export function mergeBatchesFromBatchClasses(batches, batchClasses) {
  const list = Array.isArray(batches) ? [...batches] : [];
  const ids = new Set(list.map((batch) => batchIdOf(batch)).filter(Boolean));
  (Array.isArray(batchClasses) ? batchClasses : []).forEach((cls) => {
    const nested = cls?.batch && typeof cls.batch === "object" ? cls.batch : null;
    const id = batchIdOf(nested) || batchIdOf(cls);
    if (!id || ids.has(id)) return;
    list.push(
      nested && batchIdOf(nested)
        ? nested
        : {
            organization_ems_batch_id: id,
            batch_code: nested?.batch_code ?? cls?.batch?.batch_code,
            batch_name: nested?.batch_name ?? cls?.batch?.batch_name,
            status: nested?.status ?? cls?.batch?.status,
            start_date: nested?.start_date ?? cls?.batch?.start_date,
            end_date: nested?.end_date ?? cls?.batch?.end_date,
          }
    );
    ids.add(id);
  });
  return list;
}

/**
 * Class Records batch dropdown: keep batches the trainer is assigned to,
 * including via class_trainers, even if /api/ems/batches omitted them.
 */
export function classRecordBatchesForLoggedInTrainer(
  batches,
  batchClasses,
  userData
) {
  const trainerClasses = (Array.isArray(batchClasses) ? batchClasses : []).filter(
    (cls) => emsBatchListRowMatchesLoggedInTrainer(cls, userData)
  );
  const list = mergeBatchesFromBatchClasses(batches, trainerClasses);
  const trainerBatchIds = new Set(
    trainerClasses.map((cls) => batchIdOf(cls)).filter(Boolean)
  );
  return list.filter((batch) => {
    if (emsBatchListRowMatchesLoggedInTrainer(batch, userData)) return true;
    return trainerBatchIds.has(batchIdOf(batch));
  });
}
